import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { toast } from 'sonner';
import { GET_IMAGES, GET_DEPRECATED_IMAGES } from '../graphql/queries';
import { DEPRECATE_IMAGE, SET_IMAGE_LIFECYCLE } from '../graphql/mutations';
import OsIcon from '../components/OsIcon';
import { formatArch } from '../utils/os-icons';

function statusBadge(status) {
  const map = {
    completed: 'success', active: 'success', building: 'info',
    failed: 'error', deprecated: 'warning', expired: 'error',
    scanning: 'info', pending: 'pending',
  };
  return <span className={`badge badge-${map[status] || 'pending'}`}>{status}</span>;
}

function TimelineStep({ label, date, active, completed }) {
  return (
    <div className={`timeline-step ${completed ? 'timeline-step-completed' : ''} ${active ? 'timeline-step-active' : ''}`}>
      <div className="timeline-step-dot" />
      <div className="timeline-step-label">{label}</div>
      <div className="timeline-step-date">{date ? new Date(date).toLocaleDateString() : '-'}</div>
    </div>
  );
}

export default function Lifecycle() {
  const { data: imagesData, loading: imagesLoading, error: imagesError, refetch: refetchImages } = useQuery(GET_IMAGES, { variables: { limit: 100 } });
  const { data: deprecatedData, loading: deprecatedLoading, refetch: refetchDeprecated } = useQuery(GET_DEPRECATED_IMAGES, { variables: { limit: 50 } });
  const [deprecateImage, { loading: deprecating }] = useMutation(DEPRECATE_IMAGE);
  const [setImageLifecycle, { loading: settingLifecycle }] = useMutation(SET_IMAGE_LIFECYCLE);

  const [selectedImageId, setSelectedImageId] = useState(null);
  const [deprecateForm, setDeprecateForm] = useState({ imageId: '', replacedBy: '' });
  const [lifecycleForm, setLifecycleForm] = useState({ imageId: '', retentionDays: 90, autoCleanup: true });
  const [showDeprecateForm, setShowDeprecateForm] = useState(false);
  const [showLifecycleForm, setShowLifecycleForm] = useState(false);

  const images = imagesData?.listImages?.items || [];
  const deprecatedImages = deprecatedData?.listDeprecatedImages?.items || [];

  const handleDeprecate = async (e) => {
    e.preventDefault();
    if (!deprecateForm.imageId) return;
    try {
      await deprecateImage({
        variables: { imageId: deprecateForm.imageId, replacedBy: deprecateForm.replacedBy || null },
      });
      toast.success('Image deprecated successfully.');
      setShowDeprecateForm(false);
      setDeprecateForm({ imageId: '', replacedBy: '' });
      refetchImages();
      refetchDeprecated();
    } catch (err) {
      toast.error(`Failed to deprecate image: ${err.message}`);
    }
  };

  const handleSetLifecycle = async (e) => {
    e.preventDefault();
    if (!lifecycleForm.imageId) return;
    try {
      await setImageLifecycle({
        variables: {
          input: {
            imageId: lifecycleForm.imageId,
            retentionDays: parseInt(lifecycleForm.retentionDays, 10),
            autoCleanup: lifecycleForm.autoCleanup,
          },
        },
      });
      toast.success('Lifecycle settings saved.');
      setShowLifecycleForm(false);
      setLifecycleForm({ imageId: '', retentionDays: 90, autoCleanup: true });
      refetchImages();
    } catch (err) {
      toast.error(`Failed to set lifecycle: ${err.message}`);
    }
  };

  if (imagesLoading) return <div className="loading">Loading images...</div>;
  if (imagesError) return <div className="error">Failed to load images: {imagesError.message}</div>;

  return (
    <div>
      <h2 className="page-title">Image Lifecycle Management</h2>

      <div className="card">
        <h3>Lifecycle Timeline</h3>
        <p style={{ color: '#636e72', fontSize: 13, marginBottom: 16 }}>
          Images progress through the following lifecycle stages:
        </p>
        <div className="lifecycle-timeline">
          <TimelineStep label="Created" date={null} completed active={false} />
          <TimelineStep label="Active" date={null} completed active />
          <TimelineStep label="Deprecated" date={null} completed={false} active={false} />
          <TimelineStep label="Expired" date={null} completed={false} active={false} />
        </div>
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3>All Images</h3>
          <div className="actions" style={{ marginTop: 0 }}>
            <button className="btn btn-primary" style={{ fontSize: 12, padding: '6px 14px' }}
              onClick={() => setShowDeprecateForm(!showDeprecateForm)}>
              Deprecate Image
            </button>
            <button className="btn" style={{ fontSize: 12, padding: '6px 14px', background: '#6c5ce7', color: '#fff' }}
              onClick={() => setShowLifecycleForm(!showLifecycleForm)}>
              Set Retention
            </button>
          </div>
        </div>

        {showDeprecateForm && (
          <form onSubmit={handleDeprecate} style={{ marginBottom: 16, padding: 16, background: '#f5f6fa', borderRadius: 8 }}>
            <div className="form-row">
              <div className="form-group">
                <label>Image to Deprecate</label>
                <select value={deprecateForm.imageId} onChange={(e) => setDeprecateForm({ ...deprecateForm, imageId: e.target.value })}>
                  <option value="">Select an image...</option>
                  {images.map((img) => (
                    <option key={img.id} value={img.id}>{img.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Replaced By (optional)</label>
                <select value={deprecateForm.replacedBy} onChange={(e) => setDeprecateForm({ ...deprecateForm, replacedBy: e.target.value })}>
                  <option value="">None</option>
                  {images.filter((img) => img.id !== deprecateForm.imageId).map((img) => (
                    <option key={img.id} value={img.id}>{img.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="actions" style={{ marginTop: 8 }}>
              <button className="btn btn-danger" type="submit" disabled={deprecating || !deprecateForm.imageId}>
                {deprecating ? 'Deprecating...' : 'Confirm Deprecation'}
              </button>
              <button className="btn" type="button" style={{ background: '#dfe6e9' }} onClick={() => setShowDeprecateForm(false)}>Cancel</button>
            </div>
          </form>
        )}

        {showLifecycleForm && (
          <form onSubmit={handleSetLifecycle} style={{ marginBottom: 16, padding: 16, background: '#f5f6fa', borderRadius: 8 }}>
            <div className="form-row">
              <div className="form-group">
                <label>Image</label>
                <select value={lifecycleForm.imageId} onChange={(e) => setLifecycleForm({ ...lifecycleForm, imageId: e.target.value })}>
                  <option value="">Select an image...</option>
                  {images.map((img) => (
                    <option key={img.id} value={img.id}>{img.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Retention (days)</label>
                <input type="number" value={lifecycleForm.retentionDays}
                  onChange={(e) => setLifecycleForm({ ...lifecycleForm, retentionDays: e.target.value })} min="1" />
              </div>
            </div>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={lifecycleForm.autoCleanup}
                  onChange={(e) => setLifecycleForm({ ...lifecycleForm, autoCleanup: e.target.checked })} />
                Enable auto-cleanup on expiration
              </label>
            </div>
            <div className="actions" style={{ marginTop: 8 }}>
              <button className="btn btn-primary" type="submit" disabled={settingLifecycle || !lifecycleForm.imageId}>
                {settingLifecycle ? 'Saving...' : 'Save Lifecycle Settings'}
              </button>
              <button className="btn" type="button" style={{ background: '#dfe6e9' }} onClick={() => setShowLifecycleForm(false)}>Cancel</button>
            </div>
          </form>
        )}

        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Blueprint</th>
              <th>Architecture</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {images.map((img) => (
              <tr key={img.id} className={selectedImageId === img.id ? 'row-selected' : ''}>
                <td>{img.name}</td>
                <td><OsIcon name={img.blueprintName} showLabel size={16} /></td>
                <td>{formatArch(img.architecture)}</td>
                <td>{statusBadge(img.status)}</td>
                <td>{img.createdAt ? new Date(img.createdAt).toLocaleDateString() : '-'}</td>
                <td>
                  <button className="btn btn-primary" style={{ padding: '4px 12px', fontSize: 12 }}
                    onClick={() => setSelectedImageId(selectedImageId === img.id ? null : img.id)}>
                    {selectedImageId === img.id ? 'Hide' : 'Details'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {deprecatedImages.length > 0 && (
        <div className="card">
          <h3>Deprecated Images</h3>
          {deprecatedLoading ? (
            <div className="loading">Loading deprecated images...</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Blueprint</th>
                  <th>Architecture</th>
                  <th>Status</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {deprecatedImages.map((img) => (
                  <tr key={img.id}>
                    <td>{img.name}</td>
                    <td><OsIcon name={img.blueprintName} showLabel size={16} /></td>
                    <td>{formatArch(img.architecture)}</td>
                    <td>{statusBadge(img.status)}</td>
                    <td>{img.createdAt ? new Date(img.createdAt).toLocaleDateString() : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
